<template>
  <input
    ref="inputRef"
    :id="id"
    v-model="displayValue"
    type="tel"
    :class="inputClass"
    :placeholder="placeholder"
    :required="required"
    :disabled="disabled"
    autocomplete="tel"
    @input="handleInput"
    @paste="handlePaste"
    @blur="handleBlur"
    @focus="handleFocus"
  />
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '(555) 123-4567'
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  class: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);

const inputRef = ref(null);
const displayValue = ref('');
const isFocused = ref(false);

// Default input classes
const inputClass = computed(() => {
  return props.class || 'appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm dark:bg-gray-700 dark:text-white';
});

// Clean phone number by removing all non-numeric characters and handling international formats
const cleanPhoneNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-numeric characters
  let cleaned = value.replace(/\D/g, '');
  
  // Handle international formats - remove leading 1 if it's 11 digits
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    cleaned = cleaned.substring(1);
  }
  
  // Take only the first 10 digits
  return cleaned.substring(0, 10);
};

// Format phone number with mask (###) ###-####
const formatPhoneNumber = (value) => {
  const cleaned = cleanPhoneNumber(value);
  
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

// Get cursor position after formatting
const getCursorPosition = (oldValue, newValue, oldCursor) => {
  // Count non-digit characters before cursor in old value
  const oldPrefix = oldValue.substring(0, oldCursor);
  const digitsBeforeCursor = (oldPrefix.match(/\d/g) || []).length;
  
  // Find position in new value that has same number of digits before it
  let digitCount = 0;
  for (let i = 0; i < newValue.length; i++) {
    if (/\d/.test(newValue[i])) {
      digitCount++;
      if (digitCount > digitsBeforeCursor) {
        return i;
      }
    }
  }
  
  return newValue.length;
};

const handleInput = (event) => {
  const inputValue = event.target.value;
  const cursorPosition = event.target.selectionStart;
  
  // Clean and format the input
  const cleaned = cleanPhoneNumber(inputValue);
  const formatted = formatPhoneNumber(cleaned);
  
  // Update display value
  displayValue.value = formatted;
  
  // Emit the cleaned numeric value to parent
  emit('update:modelValue', cleaned);
  
  // Set cursor position after formatting
  nextTick(() => {
    if (inputRef.value) {
      const newCursorPos = getCursorPosition(inputValue, formatted, cursorPosition);
      inputRef.value.setSelectionRange(newCursorPos, newCursorPos);
    }
  });
};

const handlePaste = (event) => {
  event.preventDefault();
  
  // Get pasted content
  const pastedText = (event.clipboardData || window.clipboardData).getData('text');
  
  // Clean and format the pasted content
  const cleaned = cleanPhoneNumber(pastedText);
  const formatted = formatPhoneNumber(cleaned);
  
  // Update values
  displayValue.value = formatted;
  emit('update:modelValue', cleaned);
};

const handleBlur = () => {
  isFocused.value = false;
};

const handleFocus = () => {
  isFocused.value = true;
};

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  if (newValue !== cleanPhoneNumber(displayValue.value)) {
    displayValue.value = formatPhoneNumber(newValue);
  }
}, { immediate: true });

// Initialize display value
displayValue.value = formatPhoneNumber(props.modelValue);
</script>
